import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import DataTable from '../../components/ui/DataTable';
import TextInput from '../../components/ui/TextInput.jsx';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import useLayoutBucket from '../../hooks/useLayoutBucket';
import { drawerGeometry } from '../../components/layout/shellMetrics';
import { formatDateTime } from '../../utils/Utils';
import { getSsoEvents } from '../../services/ssoService';
import { Badge } from './config';

// "Son Girişler" — SALT OKUNUR panel (spec §10.1): destek ekibinin "X neden giremiyor?" sorusu. Form değil →
// GenericFormDrawer DEĞİL (WebhookDeliveriesDrawer ile aynı gerekçe). Sayfalama sunucuda; tarih aralığı süzgeci üstte.
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 20px 24px; border-bottom: 1px solid #E8ECF0; flex-shrink: 0;
`;
const Title = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;
const CloseBtn = styled.button`border: 0; background: transparent; cursor: pointer; display: flex;`;
const Body = styled.div`display: flex; flex-direction: column; min-height: 0; overflow: hidden; padding: 16px 24px; gap: 12px;`;
const Filters = styled.div`display: flex; gap: 12px; flex-wrap: wrap; & > * { width: 180px; }`;
const Detail = styled.span`font-family: monospace; font-size: 11px; word-break: break-all;`;

const nextDay = (d) => { const x = new Date(`${d}T00:00:00`); x.setDate(x.getDate() + 1); return x.toISOString(); };

export default function SsoEventsDrawer({ connection, onClose }) {
  const { t, i18n } = useTranslation();
  const geometry = drawerGeometry(useLayoutBucket());
  const open = Boolean(connection);
  const [query, setQuery] = useState({ pageIndex: 1, pageSize: 20, from: '', to: '' });
  const [state, setState] = useState({ rows: [], total: 0, fetching: false, failed: false });

  useEffect(() => { if (open) setQuery({ pageIndex: 1, pageSize: 20, from: '', to: '' }); }, [open, connection?.id]);

  useEffect(() => {
    if (!open) return;
    setState((s) => ({ ...s, fetching: true }));
    const params = { pageIndex: query.pageIndex, pageSize: query.pageSize };
    if (query.from) params.from = new Date(`${query.from}T00:00:00`).toISOString();
    if (query.to) params.to = nextDay(query.to);
    getSsoEvents(connection.id, params).then((res) => {
      const ok = res?.data?.status;
      const page = ok ? res.data.data : null;
      setState({ rows: page?.data || [], total: page?.totalRowCount || 0, fetching: false, failed: !ok });
    });
  }, [open, connection, query]);

  const outcomeLabel = useCallback((outcome) => {
    if (outcome === 'success') return <Badge $bg="#E6F7F1" $color="#0F7B5A">{t('sso.events.success')}</Badge>;
    if (outcome === 'breakGlass') return <Badge $bg="#FFF7E6" $color="#B26A00">{t('sso.events.breakGlass')}</Badge>;
    return <Badge $bg="#FDECEC" $color="#B42318">{t(`responseErrors.${outcome}`, { defaultValue: outcome })}</Badge>;
  }, [t]);

  const columns = useMemo(() => [
    { name: 'occurredAt', label: t('sso.events.time'), options: { sort: false, customBodyRender: (v) => formatDateTime(v, i18n.language) } },
    { name: 'email', label: t('sso.events.email'), options: { sort: false } },
    { name: 'userName', label: t('sso.events.user'), options: { sort: false } },
    { name: 'client', label: t('sso.events.client'), options: { sort: false, customBodyRender: (v) => t(`sso.events.clients.${v}`, { defaultValue: v }) } },
    { name: 'outcome', label: t('sso.events.outcome'), options: { sort: false, customBodyRender: outcomeLabel } },
    { name: 'detail', label: t('sso.events.detail'), options: { sort: false, customBodyRender: (v) => (v ? <Detail>{v}</Detail> : '') } },
  ], [t, i18n.language, outcomeLabel]);

  const handleTableChanged = useCallback((action, tableState) => {
    setQuery((q) => ({ ...q, pageIndex: (tableState?.page || 0) + 1, pageSize: tableState?.rowsPerPage || q.pageSize }));
  }, []);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size={geometry.size}
      duration={150}
      overlayOpacity={0.2}
      style={{
        display: 'flex', flexDirection: 'column', maxWidth: '100vw', minWidth: geometry.minWidth, margin: geometry.margin,
        borderRadius: geometry.borderRadius, height: geometry.height, boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
      }}
    >
      <Header>
        <Title title={connection?.displayName}>{t('sso.events.title')} · {connection?.displayName}</Title>
        <CloseBtn onClick={onClose} aria-label={t('common.close')}><X size={20} /></CloseBtn>
      </Header>
      <Body data-role="sso-events">
        <Filters>
          <TextInput label={t('sso.events.from')} type="date" value={query.from} onChange={(e) => setQuery((q) => ({ ...q, pageIndex: 1, from: e.target.value }))} />
          <TextInput label={t('sso.events.to')} type="date" value={query.to} onChange={(e) => setQuery((q) => ({ ...q, pageIndex: 1, to: e.target.value }))} />
        </Filters>
        {state.failed && <LoadErrorBanner message={t('sso.events.loadFailed')} />}
        {open && (
          <DataTable
            title={t('sso.events.title')}
            fetching={state.fetching}
            columns={columns}
            data={state.rows}
            serverSide
            disableFilters
            totalRowCount={state.total}
            pageIndex={query.pageIndex - 1}
            pageSize={query.pageSize}
            onTableChanged={handleTableChanged}
            tableBodyMaxHeight="calc(100vh - 320px)"
          />
        )}
      </Body>
    </Drawer>
  );
}
